export type ChipType = 'backend' | 'frontend' | 'devops' | 'tools';

export interface ChipGroup {
  label: string;
  type: ChipType;
  chips: string[];
}

export interface JobOffer {
  id: string;
  title: string;
  type: string;
  description: string;
  link: string;
  seoTitle: string;
  seoDescription: string;
  intro: string;
  contract: string;
  level: string;
  location: string;
  startDate: string;
  salary: string;
  benefits: string;
  missions: string[];
  dailyLife: string[];
  profile: string[];
  perks: string[];
  techStack?: ChipGroup[];
  applyUrl?: string;
}

export const jobs: JobOffer[] = [
  {
    id: "fullstack-alternance",
    title: "Développeur·euse Full-Stack en alternance",
    type: "Alternance",
    description:
      "Écrire du code utilisé par de vraies organisations sur la durée. Du cadrage métier au déploiement, dans une équipe à taille humaine à Strasbourg.",
    link: "/job/fullstack-alternance",
    seoTitle: "Développeur·euse Full-Stack en alternance — Thinkfab",
    seoDescription:
      "Rejoignez Thinkfab en alternance comme développeur·euse Full-Stack à Strasbourg. Projets logiciels sur mesure, stack PHP / Symfony / React, encadrement senior, rentrée 2026.",
    intro:
      "Tu vas écrire du code utilisé par de vraies organisations sur la durée. Au sein d'une équipe à taille humaine qui conçoit des solutions logicielles sur mesure pour des organisations exigeantes — du cadrage métier au déploiement en production.",
    contract: "Alternance (apprentissage ou contrat pro)",
    level: "Bac+3 à Bac+5 / Diplôme d'ingénieur EPITECH",
    location: "Strasbourg (67) — 3 jours présentiel + 2 jours télétravail",
    startDate: "Rentrée 2026 (septembre)",
    salary: "Barèmes légaux apprentissage / contrat pro + revalorisation selon profil",
    benefits:
      "Tickets restaurants 11,50 €/jour (60 % pris en charge par l'employeur), mutuelle prise en charge à 90 %, transports 50 %",
    missions: [
      "**Développer des fonctionnalités** sur des produits logiciels conçus sur mesure pour nos clients — applications métier, plateformes de gestion, outils internes à fort enjeu",
      "**Participer au cycle complet** : compréhension du besoin, conception, développement, tests, mise en production. Pas de découpage artificiel entre dev et accompagnement client.",
      "**Écrire du code testé** (le test coverage n'est pas négociable chez nous) et passer tes PR en revue de code assistée",
      "**Participer aux choix d'architecture** sur nos chantiers d'industrialisation Domain-Driven Design",
    ],
    dailyLife: [
      "**Équipe** : 6 personnes aujourd'hui, 14 visées d'ici 2030. Tu es **encadré·e par un développeur senior** qui te débloque, relit ton code et te transmet les standards.",
      "**Stack** : PHP 8 / Symfony 7 / API Platform côté back, React / TypeScript / Vitejs côté front. **Mobile** : React Native / Expo selon les périmètres. Outils du quotidien : Git, Jira, Confluence.",
      "**IA dans le codage** : adoption généralisée chez nous — Claude Code financé par l'entreprise. On attend un usage **avec discernement** : lire le code généré, comprendre, valider — la lecture critique fait partie du métier. C'est ce qu'on cherche à transmettre.",
      "**Méthode** : Domain-Driven Design en cours d'industrialisation, architecture hexagonale, tests automatisés.",
      "**Rituels** : sprints de 2 semaines, lancement de sprint avec estimation par ticket, daily quotidien, revues de code, rétro en fin de sprint pour l'amélioration continue. Tu y participes pleinement et tes remontées sont attendues.",
      "**Posture conseil + dev** : nous accompagnons nos clients sur la durée, ce qui implique de comprendre leur métier au-delà du seul code. Cette dimension fait partie de la formation.",
    ],
    profile: [
      "Un **solide socle algo et code**",
      "La capacité à **lire du code que tu n'as pas écrit** et à t'intégrer dans une base existante sans tout réécrire",
      "La **curiosité** des sujets d'architecture, de qualité, de dette technique — au-delà de la seule vitesse de production",
      "L'**envie de comprendre les utilisateurs** au-delà du code : ce qui rend un produit utile dans la pratique",
      "Si tu as déjà touché à un projet avec utilisateurs réels (stage, projet perso significatif, contribution open-source), c'est un plus concret",
      "Un plus si tu coches l'un de ces critères, signale-le nous : **affinités React Native / Expo** ou **affinités DevOps** (tu pourras en faire profiter l'équipe sur les chantiers d'industrialisation), contributions open-source, projets perso documentés.",
    ],
    perks: [
      "**Tu travailles sur des produits utilisés** par de vraies organisations sur la durée",
      "**Une variété de contextes métier** : selon les chantiers du moment, tu touches à des domaines différents (santé numérique, gestion d'organisation, outils sectoriels). De quoi développer une vraie polyvalence.",
      "**Tu auras de l'autonomie tôt**, parce que l'équipe est petite et que la confiance se construit vite",
      "**Modèle stage / alternance → CDI éprouvé** chez Thinkfab.",
      "**Tu échanges directement avec le dirigeant** sur les sujets produit — tes remontées arrivent sans intermédiaire",
    ],
    techStack: [
      { label: "Backend", type: "backend", chips: ["PHP 8", "Symfony 7", "API Platform", "PHPUnit"] },
      { label: "Frontend", type: "frontend", chips: ["React", "TypeScript", "Vitejs"] },
      { label: "Mobile", type: "frontend", chips: ["React Native", "Expo"] },
      { label: "Méthode", type: "devops", chips: ["DDD", "Architecture hexagonale", "Tests automatisés"] },
      { label: "Outils", type: "tools", chips: ["Git", "Jira", "Confluence", "Claude Code"] },
    ],
    applyUrl: "https://www.linkedin.com/company/thinkfab/inbox/",
  },
  {
    id: "chef-projet-po-sante",
    title: "Chef·fe de projet / Product Owner santé en alternance",
    type: "Alternance",
    description:
      "Piloter un produit qui sert le diagnostic du cancer en France. Cadrage avec les médecins, animation des rituels agiles, projet d'envergure nationale financé France 2030.",
    link: "/job/chef-projet-po-sante",
    seoTitle: "Chef·fe de projet / Product Owner santé en alternance — Thinkfab",
    seoDescription:
      "Rejoignez Thinkfab en alternance comme Chef·fe de projet / PO sur Impulsion, plateforme d'aide au diagnostic en anatomopathologie. Strasbourg, projet financé France 2030, rentrée 2026.",
    intro:
      "Tu vas piloter un produit qui sert le diagnostic du cancer en France. Du cadrage métier concret, des arbitrages, des utilisateurs en hôpital qui dépendent de toi. Cible : profils en double cursus (école dev + spécialisation gestion de projet ou MBA), Tech Lead juniors avec appétence produit, ou candidats ayant fait un Mastère Spécialisé en gestion de projet.",
    contract: "Alternance (apprentissage ou contrat pro)",
    level:
      "Bac+5 / Diplôme d'ingénieur avec spécialisation ou expérience produit / Mastère Spécialisé en gestion de projet",
    location: "Strasbourg (67) — 3 jours présentiel + 2 jours télétravail",
    startDate: "Rentrée 2026 (septembre)",
    salary: "Barèmes légaux apprentissage / contrat pro + revalorisation selon profil",
    benefits:
      "Tickets restaurants 11,50 €/jour (60 % pris en charge par l'employeur), mutuelle prise en charge à 90 %, transports 50 %, prime de réussite en fin d'alternance",
    missions: [
      "**Cadrer des fonctionnalités** sur la plateforme Impulsion en travaillant directement avec les médecins anatomopathologistes utilisateurs (ateliers, recueil de besoins, formalisation)",
      "**Rédiger les user stories** et tenir le backlog produit avec le dirigeant (qui reste PO produit) — tu prends en charge **la gestion opérationnelle de la PO** : rituels, suivi, priorisation au quotidien",
      "**Animer les rituels agiles** (daily, planning, rétrospective) avec une équipe de 3 à 5 développeurs",
      "**Coordonner avec les partenaires industriels et institutionnels**",
      "**Participer aux comités de pilotage** clients et consortium en porteur de sujets",
    ],
    dailyLife: [
      "**Équipe** : 6 personnes aujourd'hui, 14 visées d'ici 2030.",
      "**Tu travailles en binôme avec le dirigeant** sur la dimension produit et avec **les développeurs senior** sur la dimension technique",
      "**Domaine** : anatomopathologie, télémédecine, comptes rendus médicaux structurés (CRSS), interopérabilité hospitalière (FHIR). Beaucoup à apprendre — on t'accompagne, c'est l'objet de l'alternance.",
      "**Conformité** au cœur de la pratique : RGPD santé, HDS, MDR, IA Act",
      "**Rituels** : sprints de 2 semaines, planning et estimation par ticket, daily, rétro de fin de sprint. C'est toi qui les animes en lien avec le PDG.",
      "**Outils** : Jira, Confluence, BMAD pour la méthodologie produit assistée par IA",
    ],
    profile: [
      "Tu sais **organiser, prioriser, dire non** quand c'est nécessaire",
      "Tu es à l'aise pour **expliquer un sujet technique en termes métier** et inversement — ton background dev est un atout là-dedans",
      "Tu as une **vraie capacité d'écoute** des utilisateurs (les médecins ne parlent pas comme les développeurs — c'est le sujet)",
      "Tu es **curieux·euse de la santé** : tu n'as pas besoin d'être médecin, mais tu acceptes d'apprendre le vocabulaire métier",
      "Toute expérience préalable en gestion de projet (associative, étudiante, stage, lead tech) est un plus concret",
    ],
    perks: [
      "**Un poste à enjeu** : ton rôle, c'est de **soulager le pilotage** d'une boîte en croissance et d'apprendre un métier complet",
      "**Un projet d'envergure nationale** financé France 2030",
      "**Trajectoire claire** : si l'alternance se passe bien, on construit une suite (CDI dans la fonction, ou évolution vers un autre rôle de pilotage). Notre modèle stage / alternance → CDI fonctionne en pratique, pas en théorie.",
      "**Apprentissage exigeant** : tu seras au cœur des décisions, l'équipe est petite et le rythme te permettra d'apprendre vite.",
    ],
    techStack: [
      { label: "Domaine", type: "backend", chips: ["Anatomopathologie", "Télémédecine", "FHIR", "CRSS"] },
      { label: "Conformité", type: "devops", chips: ["RGPD santé", "HDS", "MDR", "IA Act"] },
      { label: "Outils produit", type: "tools", chips: ["Jira", "Confluence", "BMAD"] },
    ],
    applyUrl: "https://www.linkedin.com/company/thinkfab/inbox/",
  },
];
